import React, { FC, useEffect, useState } from 'react';
import locales from '../../../../../../../../content.json';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import EveryDoggyAPIServices from '../../../../../../../../utils/services/EveryDoggyAPIServices';
import errorLogger from '../../../../../../../../utils/errorLogger/appErrorLogger';
import { options, paymentRequestOptions } from './constants';
import { PAYMENT_SYSTEM } from './types';
import ComposeAppleGoogleViewModel from './viewModel';
import { updatePurchaseFlow } from '../../../../../../../../reduxStore/navigation/actions';
import { ToastContainer } from 'react-toastify';
import s from './index.module.scss';
import Delimiter from '../delimiter';
import { RootState } from '../../../../../../../../store';
import { useSelector, useDispatch } from 'react-redux';
import { analyticsEventsTracker, EVENT_TRACKERS, GTMOnboardingFunnelEvent } from '../../../../../../../../utils/analytics';
import applePay from './img/aplePay.png';
import gpl from './img/gpl.png';
import { EmptyObject } from '@reduxjs/toolkit';

let productName;
let productAmount;
let paymentSystemValue;
let productType: string;

const AppleGooglePayment: FC = () => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentSystem, setPaymentSystem] = useState<PAYMENT_SYSTEM>(PAYMENT_SYSTEM.BROWSER_SAVED_CARD);
  const [appleError, setAppleError] = useState('');
  const viewModel = ComposeAppleGoogleViewModel(window.location.href, EveryDoggyAPIServices, errorLogger);
  const noPaypal = useSelector((state: RootState) => state.data.noPaypal);
  const productAmountActive = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.price : '';
  });
  const productNameActive = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.product_key : '';
  });
  const productIdActive = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.id : '';
  });

  const dispatch = useDispatch();
  const togglePurchaseFlow = (upselPDF: boolean) => {
    dispatch(updatePurchaseFlow({upselPDF: upselPDF}));
  };

  useEffect(() => {
    if(productAmountActive && productNameActive) {
      productAmount = productAmountActive;
      productName = productNameActive;
      if (productName === 'puppy_guide') {
        productType = 'puppy';
      } else {
        productType = 'adult';
      }
    }
  }, [productNameActive, productAmountActive]);

  useEffect(() => {
    paymentSystemValue = paymentSystem;
  }, [paymentSystem]);

  useEffect(() => {
    const label = locales[window.userLang].get_your_personalized_plan;
    const amount = +productAmount?.replace('.', '');

    if (stripe && amount) {
      if (paymentRequest) {
        paymentRequest.update({
          total: {
            label,
            amount,
          },
        });
      } else {
        const pr = stripe.paymentRequest(paymentRequestOptions(label, amount));

        pr.canMakePayment().then(result => {
          if (result) {
            if(!window.paymentRequestApplePayValue) {
              window.paymentRequestApplePayValue = true;
            }
            setPaymentRequest(pr);
            result.applePay && setPaymentSystem(PAYMENT_SYSTEM.APPLE_PAY);
            result.googlePay && setPaymentSystem(PAYMENT_SYSTEM.GOOGLE_PAY);
            if(result.applePay) {
              window.paymentApplePay = true;
            }
            if(result.googlePay) {
              window.paymentApplePay = false;
            }
            return;
          }
          if(window.paymentRequestApplePayValue) {
            window.paymentRequestApplePayValue = false;
          }
          setPaymentSystem(PAYMENT_SYSTEM.BROWSER_SAVED_CARD);
        });

        pr.on('cancel', async () => {
          analyticsEventsTracker.trackWithTarget(
            GTMOnboardingFunnelEvent.paymentAttemptClosed({
              payment_system: paymentSystemValue,
              product_name: productName,
            }),
          EVENT_TRACKERS.GTM);
        });

        pr.on('paymentmethod', async e => {
          
          viewModel
            .processPaymentAction(e.paymentMethod.id, productIdActive, e.payerEmail, productName, paymentSystemValue)
            .then((result: EmptyObject) => {
              if(result) {
                e.complete("success");
              }
              analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.paymentSuccessfulPdf(productName, productType, paymentSystemValue, productAmount, productIdActive), EVENT_TRACKERS.GTM);
              setTimeout(() => {
                togglePurchaseFlow(true);
              }, 1000);
            })
            .catch(error => {
              const applepayWarnings = document.querySelector('.applepayWarnings') as HTMLElement;
              if (error.message && error.message.includes('Digital product purchase')) {
                applepayWarnings.innerHTML = 'You have already purchased this product.';
              } else {
                applepayWarnings.innerHTML = `${error.message || 'Something went wrong'}`;
              }
            });
        });
      }
    }
  }, [productAmountActive, productNameActive, stripe, paymentRequest]);

  return (
    paymentRequest && (
      <div className={s.button}>
        {noPaypal && <h2 className={s.title}>{locales[window.userLang].checkout_title}</h2>}
        <div className={s.buttonWrapper}>
          <div className={s.customBtn}>
            <p>{locales[window.userLang].pdfUpselling_15}</p>
            <img src={paymentSystem === PAYMENT_SYSTEM.APPLE_PAY ? applePay : gpl} alt='apple Pay' />
          </div>
          <PaymentRequestButtonElement 
            onClick={() => {
              const event = GTMOnboardingFunnelEvent.paymentIntentionPdf(
                productName,
                productType,
                paymentSystem,
                productAmount,
                productIdActive,
              )
              analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
            }}
            options={options(paymentRequest)} 
          />
        </div>
        {appleError && <div className={s.error}>{appleError}</div>}
        <ToastContainer
          position="top-center"
          autoClose={false}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        {noPaypal && <Delimiter />}
      </div>
    )
  );
};

export default AppleGooglePayment;
